import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const NavratrotsavNocForm2024 = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? 'Navratrotsav_NOC_2024.pdf' : 'Navratrotsav_NOC_2024_EN.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm;
            }
            body {
              background-color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div className="w-full overflow-x-auto flex justify-center">
        <div ref={componentRef} className="bg-white text-black shadow-lg print:shadow-none relative" style={{ width: '210mm' }}>
          
          {/* === PAGE 1 === */}
          <div className="px-10 py-10 mx-auto relative box-border flex flex-col" style={{ width: '210mm', minHeight: '297mm', maxHeight: '297mm', overflow: 'hidden' }}>
            
            <div className="flex justify-center mb-1 mt-2">
               <h2 className="text-[17px] text-red-600 font-medium">{language === 'marathi' ? 'सार्वजनिक नवरात्रौत्सव - २०२४' : 'Public Navratrotsav - 2024'}</h2>
            </div>

            <div className="text-center mb-6 mt-1">
              <h1 className="text-2xl font-bold border-b-2 border-black inline-block pb-1">
                {language === 'marathi' ? 'ना हरकत प्रमाणपत्रासाठी करावयाचा अर्ज' : 'Application for No Objection Certificate'}
              </h1>
            </div>

            <div className="flex justify-between items-end mb-4 text-[15px]">
              <p>{language === 'marathi' ? 'प्रति,' : 'To,'}</p>
              <p className="flex items-end">
                <span className="mr-2">{language === 'marathi' ? 'दिनांक:' : 'Date:'}</span>
                <span className="border-b border-dashed border-black w-8 inline-block"></span>
                <span className="mx-1">/</span>
                <span className="border-b border-dashed border-black w-16 inline-block"></span>
                <span className="ml-1">{language === 'marathi' ? '/२०२४' : '/2024'}</span>
              </p>
            </div>

            <div className="mb-6 text-[15px] leading-loose">
              <p className="font-bold mb-1">{language === 'marathi' ? 'सरपंच / ग्रामपंचायत अधिकारी,' : 'Sarpanch / Gram Panchayat Officer,'}</p>
              
              <p className="flex items-end mb-1">
                <span className="shrink-0 mr-1">{language === 'marathi' ? 'ग्रामपंचायत कार्यालय' : 'Gram Panchayat Office'}</span>
                <span className="border-b border-dashed border-black w-64"></span>
              </p>
              <p className="flex items-end">
                <span className="shrink-0 mr-1">{language === 'marathi' ? 'ता.' : 'Tal.'}</span>
                <span className="border-b border-dashed border-black w-48"></span>
                <span className="shrink-0 mx-2">{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
                <span className="border-b border-dashed border-black w-48"></span>
              </p>
            </div>

            <div className="text-center mb-6 text-[16px] font-bold">
               <p>{language === 'marathi' ? 'विषय :- सार्वजनिक नवरात्रौत्सव/दुर्गापूजा उत्सव २०२४ - ना हरकत प्रमाणपत्र' : 'Subject :- Public Navratrotsav/Durgapuja Festival 2024 - No Objection Certificate'}</p>
               <p className="mt-1">{language === 'marathi' ? 'मिळणेबाबत.' : 'Regarding getting.'}</p>
            </div>

            <div className="pl-12 mb-6 text-[15px] leading-loose">
               <p className="flex items-end mb-1">
                 <span className="shrink-0 font-bold mr-2">{language === 'marathi' ? 'अर्जदार :-' : 'Applicant :-'}</span>
                 <span className="shrink-0 mr-1">{language === 'marathi' ? 'श्री. / सौ.' : 'Mr. / Mrs.'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
               </p>
               <p className="flex items-end pl-16 mb-1">
                 <span className="shrink-0 mr-2">{language === 'marathi' ? 'रा.' : 'Res.'}</span>
                 <span className="border-b border-dashed border-black w-64"></span>
                 <span className="shrink-0 mx-2">{language === 'marathi' ? 'मो. नं.' : 'Mob. No.'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
               </p>
               <p className="flex items-end pl-10 mt-3">
                 <span className="shrink-0 mr-2">{language === 'marathi' ? 'अध्यक्ष/सचिव' : 'President/Secretary'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
                 <span className="shrink-0 mx-1">{language === 'marathi' ? 'सार्वजनिक नवरात्रौत्सव मंडळ' : 'Public Navratrotsav Mandal'}</span>
                 <span className="border-b border-dashed border-black w-24"></span>
               </p>
            </div>

            <div className="mb-6 text-[15px] leading-loose">
              <p className="mb-3">{language === 'marathi' ? 'मा महोदय,' : 'Respected Sir,'}</p>
              <p className="pl-12 text-justify">
                 {language === 'marathi' ? 'वरील विषयान्वये सविनय अर्ज सादर करतो / करते की, सार्वजनिक नवरात्रौत्सव -२०२४ साठी' : 'With reference to the above subject, I humbly submit that for Public Navratrotsav -2024,'}
              </p>
              <div className="flex flex-wrap items-end mt-1">
                 <span className="border-b border-dashed border-black flex-grow"></span>
                 <span className="mx-1">{language === 'marathi' ? 'सार्वजनिक नवरात्रौत्सव मंडळ मौजे' : 'Public Navratrotsav Mandal Mauje'}</span>
                 <span className="border-b border-dashed border-black w-48"></span>
              </div>
              <div className="flex flex-wrap items-end mt-1">
                 <span className="mr-1">{language === 'marathi' ? 'ता.' : 'Tal.'}</span>
                 <span className="border-b border-dashed border-black w-48"></span>
                 <span className="mx-2">{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
                 <span className="border-b border-dashed border-black w-48"></span>
                 <span className="mx-1">{language === 'marathi' ? 'हे आमचे मंडळ' : 'this is our Mandal'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
              </div>
              <div className="flex flex-wrap items-end mt-1 mb-4">
                 <span className="border-b border-dashed border-black w-48"></span>
                 <span className="ml-1">{language === 'marathi' ? 'या ठिकाणी सार्वजनिक नवरात्रौत्सव साजरा करणार आहे.' : 'will celebrate Public Navratrotsav at this place.'}</span>
              </div>

              <p className="pl-12 text-justify mt-4">
                 {language === 'marathi' ? 'शासनाने व ग्रामपंचायतीने विहीत केलेल्या अटी-शर्ती व मार्गदर्शक सुचनांचे पालन करणे आमच्यावर' : 'It is binding on us to follow the terms and conditions and guidelines prescribed by the Government and Gram Panchayat.'}
              </p>
              <p className="text-justify mt-1">
                 {language === 'marathi' ? 'बंधनकारक राहील याची मला/आम्हांला जाणीव आहे. त्याचे उल्लंघन झाल्यास त्यास सर्वस्वी मी/आम्ही' : 'I/we are aware of this. In case of its violation, I/we will be entirely responsible.'}
              </p>
              <p className="text-justify mt-1 mb-4">
                 {language === 'marathi' ? 'जबाबदार राहू.' : ''}
              </p>

              <p className="pl-12 mt-4">
                 {language === 'marathi' ? 'करिता मला/आम्हांला ना हरकत प्रमाणपत्र देण्यात यावे ही विनंती.' : 'Therefore, it is requested that I/we be given a No Objection Certificate.'}
              </p>
            </div>

            <div className="flex justify-end text-[15px] mt-auto">
               <div className="flex flex-col space-y-6 w-[350px]">
                  <p className="flex items-end">
                    <span className="shrink-0 mr-2">{language === 'marathi' ? 'अर्जदाराची सही :' : 'Signature of Applicant :'}</span>
                    <span className="border-b border-dashed border-black flex-grow"></span>
                  </p>
                  <p className="flex items-end">
                    <span className="shrink-0 mr-2">{language === 'marathi' ? 'नाव :' : 'Name :'}</span>
                    <span className="border-b border-dashed border-black flex-grow"></span>
                  </p>
               </div>
            </div>

            <div className="mt-8 text-[15px] pb-4">
               <p>{language === 'marathi' ? 'सोबत:' : 'Enclosures:'}</p>
            </div>

          </div>

          <div className="html2pdf__page-break"></div>

          {/* === PAGE 2 === */}
          <div className="px-10 py-10 mx-auto relative box-border flex flex-col" style={{ width: '210mm', minHeight: '297mm', maxHeight: '297mm', overflow: 'hidden' }}>
            
            <div className="flex justify-end mb-6 text-[15px]">
               <div className="flex flex-col space-y-1 w-[280px]">
                 <p className="flex items-end">
                   <span className="shrink-0 mr-1">{language === 'marathi' ? 'कार्यालय ग्रा.पं.' : 'Office G.P.'}</span>
                   <span className="border-b border-dashed border-black flex-grow"></span>
                 </p>
                 <p className="flex items-end">
                   <span className="shrink-0 mr-1">{language === 'marathi' ? 'ता.' : 'Tal.'}</span>
                   <span className="border-b border-dashed border-black w-24"></span>
                   <span className="shrink-0 mx-1">{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
                   <span className="border-b border-dashed border-black flex-grow"></span>
                 </p>
                 <p className="flex items-end font-bold">
                   <span className="mr-2">{language === 'marathi' ? 'दिनांक :' : 'Date :'}</span>
                   <span className="border-b border-dashed border-black w-8 inline-block font-normal"></span>
                   <span className="mx-1 font-normal">/</span>
                   <span className="border-b border-dashed border-black w-16 inline-block font-normal"></span>
                   <span className="ml-1">{language === 'marathi' ? '/२०२४' : '/2024'}</span>
                 </p>
               </div>
            </div>

            <div className="text-center mb-8">
               <h1 className="text-2xl font-bold border-b-2 border-black inline-block pb-1">
                 {language === 'marathi' ? 'ना हरकत प्रमाणपत्र' : 'No Objection Certificate'}
               </h1>
            </div>

            <div className="mb-6 text-[15px] leading-loose">
               <div className="flex flex-wrap items-end">
                  <span className="pl-12 mr-1">{language === 'marathi' ? 'प्रमाणपत्र देण्यात येते की, अर्जदार श्री./श्रीमती' : 'This is to certify that, Applicant Mr./Mrs.'}</span>
                  <span className="border-b border-dashed border-black flex-grow"></span>
               </div>
               <div className="flex flex-wrap items-end mt-1">
                  <span className="mr-1">{language === 'marathi' ? 'अध्यक्ष/सचिव' : 'President/Secretary'}</span>
                  <span className="border-b border-dashed border-black flex-grow"></span>
                  <span className="ml-1">{language === 'marathi' ? 'दुर्गामाता नवरात्र उत्सव मंडळ' : 'Durgamata Navratrotsav Mandal'}</span>
               </div>
               <div className="flex flex-wrap items-end mt-4">
                  <span className="mr-1">{language === 'marathi' ? 'मौजे' : 'Mauje'}</span>
                  <span className="border-b border-dashed border-black w-64"></span>
                  <span className="mx-1">{language === 'marathi' ? 'ता.' : 'Tal.'}</span>
                  <span className="border-b border-dashed border-black flex-grow"></span>
                  <span className="mx-1">{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
                  <span className="border-b border-dashed border-black w-40"></span>
               </div>
               <div className="flex flex-wrap items-end mt-1">
                  <span className="mr-1">{language === 'marathi' ? 'यांच्या अर्जानुसार' : 'as per their application'}</span>
                  <span className="border-b border-dashed border-black flex-grow"></span>
                  <span className="ml-1">{language === 'marathi' ? 'येथे' : 'here'}</span>
               </div>
               <p className="text-justify mt-1">
                  {language === 'marathi' ? 'नवरात्र उत्सव- २०२४ खालील अटी व शर्तींच्या अधिन राहून साजरा करण्यास ग्रामपंचायत कार्यालयाची काहीही' : 'the Gram Panchayat office has no objection to celebrating Navratrotsav - 2024 subject to the following terms and conditions.'}
               </p>
               <p className="text-justify mt-1">
                  {language === 'marathi' ? 'हरकत नाही.' : ''}
               </p>
            </div>

            <div className="mb-4">
               <h2 className="text-lg font-bold">{language === 'marathi' ? 'अटी व शर्ती :' : 'Terms and Conditions :'}</h2>
            </div>

            <div className="pl-6 mb-6 text-[15px] space-y-1.5 leading-relaxed">
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '१)' : '1)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'ग्रामपंचायतीने दिलेल्या ना हरकत प्रमाणपत्र व्यतिरिक्त पोलीस विभागाची स्वतंत्र परवानगी घ्यावी लागेल. तसेच आवश्यक संबंधित सर्व वरिष्ठ कार्यालयाची परवानगी घेणे बंधनकारक राहील.' : 'Apart from the NOC given by the Gram Panchayat, separate permission from the police department will have to be taken. Also, it will be mandatory to take permission from all necessary concerned higher offices.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '२)' : '2)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'सार्वजनिक आरोग्य, जीवित,सार्वजनिक मालमत्ता, शैक्षणिक संस्था तसेच पर्यावरणाला धोका होणार नाही याची दक्षता घ्यावी.' : 'Care should be taken that there will be no danger to public health, life, public property, educational institutions and the environment.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '३)' : '3)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'प्लॅस्टिक व थर्माकॉलचा वापर करता येणार नाही, निर्माल्याचे व्यवस्थापन करावे लागेल.' : 'Plastic and thermocol cannot be used, nirmalya will have to be managed.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '४)' : '4)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'ध्वनीप्रदूषण बाबत शासन नियमांचे पालन करणे बंधनकारक राहील.' : 'It will be mandatory to follow the government rules regarding noise pollution.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '५)' : '5)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'शक्य असल्यास मंडळाचे परिसरात सीसीटीव्ही कॅमेरे लावावे.' : 'If possible, CCTV cameras should be installed in the Mandal area.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '६)' : '6)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'वाहतुकीस अडथळा निर्माण होणार नाही याची दक्षता घ्यावी. रहदारी नियमन करण्यासाठी आपले स्वयंसेवक नेमावे.' : 'Care should be taken that there will be no obstruction to traffic. Volunteers should be appointed to regulate traffic.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '७)' : '7)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'शासनाने ठरवून दिलेल्या सर्व नियमांचे पालन होईल याची दक्षता घ्यावी.' : 'Care should be taken that all the rules laid down by the government will be followed.'}</p>
               </div>
               <div className="flex items-end pt-1">
                  <span className="mr-2">{language === 'marathi' ? '८)' : '8)'}</span>
                  <span className="border-b border-dashed border-black flex-grow inline-block mb-1"></span>
               </div>
            </div>

            <div className="mb-8 text-[15px] leading-relaxed">
               <p className="pl-12 text-justify">
                  {language === 'marathi' ? 'ग्रामपंचायतीने नमूद केलेल्या सर्व अटी व शर्तींचे पालन होत नाही असे आढळून आल्यास सदर ना' : 'If it is found that all the terms and conditions mentioned by the Gram Panchayat are not followed, the Gram Panchayat will have the right to cancel the said No Objection Certificate.'}
               </p>
               <p className="text-justify">
                  {language === 'marathi' ? 'हरकत प्रमाणपत्र रद्द करण्याचे अधिकार ग्रामपंचायतीला राहतील.' : ''}
               </p>
               <p className="pl-12 text-justify mt-2">
                  {language === 'marathi' ? 'करिता वरील अटी-शर्तींच्या अधिन राहून ना हरकत प्रमाणपत्र देण्यात येत आहे.' : 'Therefore, the No Objection Certificate is being issued subject to the above terms and conditions.'}
               </p>
               <p className="italic font-bold mt-2">
                  {language === 'marathi' ? 'सर्वांना नवरात्र उत्सवाच्या हार्दिक शुभेच्छा...!' : 'Hearty Navratrotsav greetings to all...!'}
               </p>
            </div>

            {/* Note the double dotted lines on this form version */}
            <div className="flex justify-between items-start mt-auto text-[15px] pb-4 px-4">
               <div className="w-1/2 flex flex-col items-center">
                  <p className="font-bold mb-4">{language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</p>
                  <p className="flex items-end">
                     <span className="mr-2">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
                     <span className="border-b border-dashed border-black w-40"></span>
                  </p>
               </div>
               <div className="w-1/2 flex flex-col items-center">
                  <p className="font-bold mb-4">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'}</p>
                  <p className="flex items-end">
                     <span className="mr-2">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
                     <span className="border-b border-dashed border-black w-40"></span>
                  </p>
               </div>
            </div>

            <div className="absolute bottom-6 left-10 text-blue-700 text-sm underline print:hidden">
              www.majhigrampanchayat.com
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default NavratrotsavNocForm2024;
